/**
 * Copyright Highway9 Networks Inc.
 *
 * Registry mapping websocket refresh events to Entity State apply actions.
 * Uniform single-collection handlers share one runner in eventContext.
 * objectTypes drive websocket → evt.emit routing (see socketRefreshHandler).
 * Emit-only exceptions (e.g. walkTest) stay in the handler.
 */
import type { Action } from "@reduxjs/toolkit";
import type { WebSocketMessage } from "~/types/WebsocketMessage";
import type { EventMap } from "../event";
import { store } from "..";
import { Types } from "~/constants/types";

import { radioActions, fetchRadios, fetchRadioUpgrades } from "../context/radioSlice";
import { radioGroupActions, fetchRadioGroups } from "../context/radioGroupSlice";
import { siteActions, fetchSites } from "../context/siteSlice";
import { edgeActions, fetchEdges, fetchEdgePools, fetchEdgeUpgrades, edgeData } from "../context/edgeSlice";
import { subscriberActions, fetchDevices, fetchDNN, fetchSubscriberDeviceInfo } from "../context/subscriberSlice";
import { deviceGroupActions, fetchDeviceGroups } from "../context/deviceGroupSlice";
import { simulatorAction, fetchSimulator, fetchUESimulators } from "../context/simulatorSlice";
import { syncProfileActions, fetchClockPools, fetchClocks } from "../context/syncProfileSlice";
import { mobilityProfileActions, fetchMobilityProfiles } from "../context/mobilityProfileSlice";
import { monitoringProfileActions, fetchMonitoringProfiles, fetchAAAProfiles } from "../context/monitoringProfileSlice";
import { alertProfileActions, fetchAlertProfiles } from "../context/alertProfileSlice";
import { userActions, fetchUsers } from "../context/userSlice";
import { settingsActions, fetchSystemSettings, fetchSupportChannel, fetchOperations } from "../context/settingsSlice";
import { accessConfigActions, fetchAccessConfigs, fetchDefaultRadioManager } from "../context/accessConfigSlice";
import { caCertActions, fetchCACertConfigs } from "../context/caCertSlice";
import { apiTokenActions, fetchApiTokens } from "../context/apiTokenSlice";
import { kpiTemplateActions, fetchMetricsTemplate } from "../context/kpiTemplateSlice";
import { hardwareActions, fetchHardware } from "../context/hardwareSlice";
import { zoneActions, fetchPolicies, fetchZones, fetchQOSProfiles } from "../context/zoneSlice";
import { zoneGroupActions, fetchZoneGroups } from "../context/zoneGroupSlice";
import { operatorActions } from "../context/operatorSlice";
import { networkActions, fetchNetwork } from "../context/networkSlice";
import { notificationActions } from "../context/notificationsSlice";
import { generateNotificationsFromOperations } from "~/components/Notifications/utils";
import { syncOperationSocketSideEffects } from "./operationsRefreshSideEffects";

export interface SocketRefreshEntry {
  /** Websocket objectType(s) that emit this event (from Types.*). */
  objectTypes: string[];
  /** Dispatch Entity State apply for the patch batch (one or more actions). */
  action: (patches: WebSocketMessage[]) => Action | Action[];
  /** Full refetch when patches are absent. */
  fetch: () => void;
  /** When true, skip apply while PAUSE_EVENTS is set (monitoring historical view). */
  pausable?: boolean;
  /** When true, fall back to fetch instead of applying patches. */
  isEmpty?: () => boolean;
  /** Optional patch rewriting before apply. */
  preprocess?: (patches: WebSocketMessage[]) => WebSocketMessage[];
  /** Optional side effects after Entity State apply (e.g. notification sync). */
  sideEffects?: (patches: WebSocketMessage[]) => void | Promise<void>;
}

const hasRadioVersionChanges = (patches: WebSocketMessage[]) =>
  patches.some((patch) => {
    if (!patch.modifiedValues || typeof patch.modifiedValues !== "object") return false;
    if ("upgradeToRelease" in patch.modifiedValues) return true;
    if (
      patch.modifiedValues.runtimeInfo &&
      typeof patch.modifiedValues.runtimeInfo === "object" &&
      "softwareVersion" in patch.modifiedValues.runtimeInfo
    ) {
      return true;
    }
    return false;
  });

const hasEdgeReleaseChanges = (patches: WebSocketMessage[]) =>
  patches.some(
    (patch) =>
      patch.modifiedValues &&
      typeof patch.modifiedValues === "object" &&
      ("currentRelease" in patch.modifiedValues || "upgradeToRelease" in patch.modifiedValues),
  );

/**
 * Uniform websocket refresh events → Entity State apply.
 * Bespoke handlers (insights, fetch-only) stay in eventContext.
 */
export const socketRefreshRegistry = {
  refreshRadios: {
    objectTypes: [Types.radio],
    action: radioActions.applySocketPatches,
    fetch: () => {
      void store.dispatch(fetchRadios());
      store.dispatch(fetchEdgeUpgrades());
    },
    pausable: true,
    sideEffects: (patches) => {
      if (hasRadioVersionChanges(patches)) {
        store.dispatch(fetchRadioUpgrades());
      }
    },
  },
  refreshRadioGroups: {
    objectTypes: [Types.radioGroup],
    action: radioGroupActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchRadioGroups({}));
    },
    pausable: true,
  },
  refreshSites: {
    objectTypes: [Types.site],
    action: siteActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchSites());
    },
    pausable: true,
  },
  refreshEdges: {
    objectTypes: [Types.edge, Types.cgwEdge],
    action: edgeActions.applySocketPatchesEdges,
    fetch: () => {
      void store.dispatch(fetchEdges());
      store.dispatch(fetchEdgeUpgrades());
    },
    pausable: true,
    isEmpty: () => edgeData(store.getState()).length === 0,
    sideEffects: (patches) => {
      if (hasEdgeReleaseChanges(patches)) {
        store.dispatch(fetchEdgeUpgrades());
      }
    },
  },
  refreshEdgePools: {
    objectTypes: [Types.edgePool],
    action: edgeActions.applySocketPatchesEdgePools,
    fetch: () => {
      store.dispatch(fetchEdgePools());
    },
    pausable: true,
  },
  refreshDevices: {
    objectTypes: [Types.device],
    action: subscriberActions.applySocketPatchesDevices,
    fetch: () => {
      store.dispatch(fetchDevices());
    },
  },
  refreshDeviceInfo: {
    objectTypes: [Types.deviceInfo],
    action: subscriberActions.applySocketPatchesDeviceInfo,
    fetch: () => {
      store.dispatch(fetchSubscriberDeviceInfo());
    },
  },
  refreshDeviceGroups: {
    objectTypes: [Types.deviceGroup],
    action: deviceGroupActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchDeviceGroups());
    },
  },
  refreshSimulator: {
    objectTypes: [Types.simulatorAppliance],
    action: simulatorAction.applySocketPatchesSimulators,
    fetch: () => {
      store.dispatch(fetchSimulator());
    },
  },
  refreshUESimulators: {
    objectTypes: [Types.ueSimulator],
    action: simulatorAction.applySocketPatchesUeSimulators,
    fetch: () => {
      store.dispatch(fetchUESimulators());
    },
  },
  refreshClockPools: {
    objectTypes: [Types.clockPool],
    action: syncProfileActions.applySocketPatchesClockPools,
    fetch: () => {
      store.dispatch(fetchClockPools());
    },
  },
  refreshClocks: {
    objectTypes: [Types.clock],
    action: syncProfileActions.applySocketPatchesClocks,
    fetch: () => {
      store.dispatch(fetchClocks());
    },
  },
  refreshMobilityProfiles: {
    objectTypes: [Types.mobilityProfile],
    action: mobilityProfileActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchMobilityProfiles());
    },
  },
  refreshMonitoringSystem: {
    objectTypes: [Types.monitoringSystem],
    action: monitoringProfileActions.applySocketPatchesMonitoring,
    fetch: () => {
      store.dispatch(fetchMonitoringProfiles());
    },
  },
  refreshAAAProfiles: {
    objectTypes: [Types.aaaServerProfile],
    action: monitoringProfileActions.applySocketPatchesAaa,
    fetch: () => {
      store.dispatch(fetchAAAProfiles());
    },
  },
  refreshAlertProfiles: {
    objectTypes: [Types.alertProfile],
    action: alertProfileActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchAlertProfiles());
    },
  },
  refreshUsers: {
    objectTypes: [Types.user],
    action: userActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchUsers());
    },
  },
  refreshSettings: {
    objectTypes: [Types.settings],
    action: settingsActions.applySocketPatchesSystem,
    fetch: () => {
      store.dispatch(fetchSystemSettings());
    },
  },
  refreshSupportChannel: {
    objectTypes: [Types.supportChannel],
    action: settingsActions.applySocketPatchesSupport,
    fetch: () => {
      store.dispatch(fetchSupportChannel());
    },
  },
  refreshAccessConfig: {
    objectTypes: [Types.accessConfig],
    action: accessConfigActions.applySocketPatchesAccessConfig,
    fetch: () => {
      store.dispatch(fetchAccessConfigs());
    },
  },
  refreshDefaultRadioManager: {
    objectTypes: [Types.defaultRadioManager],
    action: accessConfigActions.applySocketPatchesDefaultRadioManager,
    fetch: () => {
      store.dispatch(fetchDefaultRadioManager());
    },
  },
  refreshCACert: {
    objectTypes: [Types.caCert],
    action: caCertActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchCACertConfigs());
    },
  },
  refreshApiToken: {
    objectTypes: [Types.apiToken],
    action: apiTokenActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchApiTokens());
    },
  },
  refreshDNN: {
    objectTypes: [Types.dnn],
    action: subscriberActions.applySocketPatchesNetworks,
    fetch: () => {
      store.dispatch(fetchDNN());
    },
  },
  refreshMetricsTemplate: {
    objectTypes: [Types.metricsTemplate],
    action: kpiTemplateActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchMetricsTemplate());
    },
  },
  refreshHardware: {
    objectTypes: [Types.hardware],
    action: hardwareActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchHardware());
    },
  },
  refreshPolicy: {
    objectTypes: [Types.application],
    action: zoneActions.applySocketPatchesPolicies,
    fetch: () => {
      store.dispatch(fetchPolicies());
    },
  },
  refreshQOSProfile: {
    // QoS entities already carry merged QCI fields from fetch; patches update profiles only.
    // qciData is left untouched (same as the old handler reusing zoneQOSProfileQCI).
    objectTypes: [Types.qosProfile],
    action: zoneActions.applySocketPatchesQosProfiles,
    fetch: () => {
      store.dispatch(fetchQOSProfiles());
    },
  },
  refreshVMZone: {
    objectTypes: [Types.virtualMobilityZone],
    action: zoneActions.applySocketPatchesZones,
    fetch: () => {
      store.dispatch(fetchZones());
    },
  },
  refreshZoneGroup: {
    objectTypes: [Types.zoneGroup],
    action: zoneGroupActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchZoneGroups());
    },
  },
  refreshOperators: {
    objectTypes: [Types.operator],
    // Same patches apply to both the global and edge-scoped operator collections.
    action: (patches) => [
      operatorActions.applySocketPatchesOperators(patches),
      operatorActions.applySocketPatchesEdgeOperators(patches),
    ],
    // Components that need operators refetch locally when patches are absent.
    fetch: () => {},
  },
  refreshOperatorRadios: {
    objectTypes: [Types.operatorRadio],
    action: operatorActions.applySocketPatchesOperatorRadios,
    // Operator radios are fetched in context of selected operators; no blank refetch.
    fetch: () => {},
  },
  refreshNetwork: {
    objectTypes: [Types.network],
    action: networkActions.applySocketPatches,
    fetch: () => {
      store.dispatch(fetchNetwork());
    },
  },
  operations: {
    objectTypes: [Types.operation],
    action: settingsActions.applySocketPatchesOperations,
    fetch: () => {
      void store
        .dispatch(fetchOperations())
        .unwrap()
        .then((data) => {
          store.dispatch(
            notificationActions.bulkAddNotifications(generateNotificationsFromOperations(data)),
          );
        });
    },
    // After Entity State apply: notifications + session auto-download.
    sideEffects: (patches) => {
      void syncOperationSocketSideEffects(patches);
    },
  },
} satisfies Partial<Record<keyof EventMap, SocketRefreshEntry>>;

type SocketRefreshRegistry = typeof socketRefreshRegistry;
export type SocketRefreshRegistryKey = keyof SocketRefreshRegistry;

/**
 * Reverse index: websocket objectType → evt event name.
 * Built once from registry objectTypes.
 */
export const objectTypeToSocketEvent: ReadonlyMap<string, SocketRefreshRegistryKey> = (() => {
  const map = new Map<string, SocketRefreshRegistryKey>();

  for (const [eventName, entry] of Object.entries(socketRefreshRegistry)) {
    for (const objectType of entry.objectTypes) {
      map.set(objectType, eventName as SocketRefreshRegistryKey);
    }
  }

  return map;
})();

/**
 * Shared runner for uniform registry entries.
 */
export const runSocketRefresh = (
  entry: SocketRefreshEntry,
  patchData: WebSocketMessage[] | undefined,
  pauseEvents?: boolean,
) => {
  if (!patchData) {
    entry.fetch();
    return;
  }
  if (pauseEvents && entry.pausable) return;
  const patches = entry.preprocess?.(patchData) ?? patchData;
  if (entry.isEmpty?.()) {
    entry.fetch();
    return;
  }
  const actions = entry.action(patches);
  for (const action of Array.isArray(actions) ? actions : [actions]) {
    store.dispatch(action);
  }
  // Side effects run after apply so they can read patched Entity State.
  entry.sideEffects?.(patches);
};
